/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Aug 25 2023
 **/

import { useState, useMemo } from "react";
import { z } from "zod";
import _ from "lodash-es";

import { useUsers } from "@/frameworks/core/API";
import { _T } from "@rfice/language";

import { InvalidUserEmail, UserData, ValidUserEmail } from "./private/types";
import { useCreateSpaceState } from "@/frameworks/core/hooks";

const makeValidData = (res: { username: string; data: UserData }): ValidUserEmail => ({
	...res,
	valid: true
});

const makeInvalidData = (res: { username: string; message: string; data: UserData | null }): InvalidUserEmail => ({
	...res,
	valid: false,
	data: res.data || null
});

export default function useInviteMemberField() {
	const { state, groupData } = useCreateSpaceState();
	const userAPI = useUsers();

	const [inviteMemberPopover, setInviteMemberPopover] = useState(false);

	const showInviteMemberPopover = () => {
		setInviteMemberPopover(true);
	};

	const hideInviteMemberPopover = () => {
		setInviteMemberPopover(false);
	};

	const inviteUserEmailsChipData = useMemo<(ValidUserEmail | InvalidUserEmail)[]>(() => {
		return state.inviteUserEmails.reduce<(ValidUserEmail | InvalidUserEmail)[]>((acc, tag) => {
			const matchedRelatedUsersResult = (() => {
				if (state.allowOtherGroupUser) {
					return _.flatMapDeep(_.get(userAPI.relatedUsers.list.result.data, "groups"), "groupUsers").find(
						({ username }) => username === tag
					);
				} else {
					return _.get(
						_.get(userAPI.relatedUsers.list.result.data!, "groups").find(
							(obj) => obj.groupId === state.groupId
						) || { groupUsers: [] },
						"groupUsers"
					).find(({ username }) => username === tag);
				}
			})();

			const matchedAllowDomain = groupData?.allowDomains?.find((domain) => tag.includes(domain));
			const validatedEmail = z.string().email().safeParse(tag);

			if (groupData?.useDomainOption && !matchedAllowDomain) {
				acc.push(
					makeInvalidData({ username: tag, message: _T("이메일 도메인이 일치하지 않습니다."), data: null })
				);
				return acc;
			}

			if (matchedRelatedUsersResult) {
				// 중복된 이메일일 경우,
				if (acc.find((item) => item.username === tag)) {
					acc.push(makeInvalidData({ username: tag, message: _T("중복된 이메일입니다."), data: null }));
					return acc;
				}

				acc.push(
					makeValidData({
						username: tag,
						data: matchedRelatedUsersResult
					})
				);
			} else if (validatedEmail.success) {
				if (acc.find((item) => item.username === tag)) {
					acc.push(makeInvalidData({ username: tag, message: _T("중복된 이메일입니다."), data: null }));
					return acc;
				} else if (!state.allowOtherGroupUser) {
					acc.push(
						makeInvalidData({
							username: tag,
							message: _T("해당 그룹에 속한 사용자가 아닙니다."),
							data: null
						})
					);
					return acc;
				}
				acc.push(makeValidData({ username: tag, data: { name: tag, username: tag } }));
			} else {
				acc.push(
					makeInvalidData({
						username: tag,
						data: null,
						message: (() => {
							const result = z
								.string()
								.email({
									message: _T("다시 확인해주세요.")
								})
								.safeParse(tag);
							if (!result.success) {
								return result.error.issues[0].message;
							} else {
								return _T("다시 확인해주세요.");
							}
						})()
					})
				);
			}
			return acc;
		}, []);
	}, [state.inviteUserEmails, userAPI.relatedUsers.list, state.allowOtherGroupUser, state.groupId]);

	return {
		isActiveInviteMemberPopover: inviteMemberPopover,
		showInviteMemberPopover,
		hideInviteMemberPopover,
		inviteUserEmailsChipData
	};
}
